'use client';

import Image from 'next/image';
import Link from 'next/link';

import { Trans } from '@kit/ui/trans';

import type { LandingPageProps } from '../_lib/landing-page.types';

import { ClubsLocationsSection } from './components/clubs-locations';
import type { PublicLocation } from './components/clubs-locations';

import './styles.css';

/**
 * Delta (Nadena) landing page.
 *
 * Hero faithfully mirrors the client's site (nadena-nc.com): a full-bleed
 * activity-photo collage under a dark teal tint, the white نادينا / NADENA
 * lockup centered with the multicolour leaf mark, the "trial launch" badges,
 * and a green primary CTA. Assets are stored locally under
 * /public/landing-pages/delta/images so the page doesn't hot-link the client
 * domain.
 */
export default function DeltaLandingPage({
  tenant,
  initialLocations,
}: LandingPageProps) {
  return (
    <main className="delta-landing min-h-screen bg-[var(--delta-ink)]">
      <HeroSection />
      <ClubsLocationsSection
        accountId={tenant?.id}
        initialLocations={initialLocations as PublicLocation[] | undefined}
      />
    </main>
  );
}

function HeroSection() {
  return (
    <section className="relative flex min-h-screen w-full flex-col overflow-hidden">
      {/* Background collage (already duotone-toned in the source asset) */}
      <Image
        src="/landing-pages/delta/images/hero-collage.webp"
        alt=""
        fill
        priority
        sizes="100vw"
        className="object-cover"
      />

      {/* Light teal tint — keeps the white logo + CTA legible without washing
          out the activity photos behind them. */}
      <div className="absolute inset-0 bg-[var(--delta-teal-overlay)]/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--delta-ink)]/55 via-transparent to-[var(--delta-ink)]/45" />

      {/* Soft top scrim — complements the navbar's own gradient so the bright
          top edge of the collage transitions cleanly into the header. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[var(--delta-ink)]/40 to-transparent" />

      {/* Centered lockup + CTA */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pt-24 pb-20 text-center">
        <div className="delta-animate-fade-up mb-10">
          <Image
            src="/landing-pages/delta/images/logo-full.png"
            alt="نادينا — NADENA"
            width={1324}
            height={561}
            priority
            className="h-auto w-72 sm:w-96 md:w-[34rem] lg:w-[40rem]"
          />
        </div>

        <Link
          href="/book"
          className="delta-animate-fade-up delta-delay-300 inline-flex h-14 items-center justify-center rounded-full bg-[var(--delta-green)] px-12 text-base font-semibold text-white shadow-lg shadow-black/20 transition-transform duration-200 hover:scale-[1.03] hover:bg-[var(--delta-green-dark)] active:scale-[0.98] sm:text-lg"
        >
          <Trans i18nKey="common:bookCourt" defaults="Book Now" />
        </Link>
      </div>
    </section>
  );
}
